import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  FileBox,
  Info,
  CheckCircle,
} from "lucide-react";

const ProductOfArrayExceptSelf = () => {
  // Test cases
  const defaultTestCases = [
    { id: 1, name: "Example 1", array: [1, 2, 3, 4] },
    { id: 2, name: "With Zero", array: [-1, 1, 0, -3, 3] },
    { id: 3, name: "All Positive", array: [4, 5, 1, 8, 2] },
    { id: 4, name: "Mixed Numbers", array: [2, -3, 4, -1] },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [array, setArray] = useState(testCases[0].array);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function productExceptSelf(nums):", indent: 0 },
    { line: "n = nums.length", indent: 1 },
    { line: "left = new Array(n), right = new Array(n)", indent: 1 },
    { line: "result = new Array(n)", indent: 1 },
    { line: "", indent: 0 },
    { line: "// Calculate left products", indent: 1 },
    { line: "left[0] = 1", indent: 1 },
    { line: "for i = 1 to n-1:", indent: 1 },
    { line: "left[i] = left[i-1] * nums[i-1]", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Calculate right products", indent: 1 },
    { line: "right[n-1] = 1", indent: 1 },
    { line: "for i = n-2 down to 0:", indent: 1 },
    { line: "right[i] = right[i+1] * nums[i+1]", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Calculate final result", indent: 1 },
    { line: "for i = 0 to n-1:", indent: 1 },
    { line: "result[i] = left[i] * right[i]", indent: 2 },
    { line: "return result", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (nums) => {
    const n = nums.length;
    if (n === 0) return [];
    const allSteps = [];
    let stepCount = 0;

    let left = new Array(n);
    let right = new Array(n);
    let result = new Array(n);

    const addStep = (desc, hl, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description: desc,
        highlightLine: hl,
        leftProducts: [...left],
        rightProducts: [...right],
        result: [...result],
        ...details,
      });
    };

    addStep(
      "Start: Initialize three arrays for left, right, and final results.",
      2
    );

    // Left pass
    addStep("Phase 1: Calculate prefix products.", 5);
    left[0] = 1;
    addStep("Initialize left[0] = 1.", 6, { leftIndex: 0 });
    for (let i = 1; i < n; i++) {
      left[i] = left[i - 1] * nums[i - 1];
      addStep(
        `left[${i}] = left[${i - 1}] * nums[${i - 1}] = ${left[i - 1]} * ${
          nums[i - 1]
        } = ${left[i]}.`,
        8,
        { leftIndex: i }
      );
    }

    // Right pass
    addStep("Phase 2: Calculate suffix products.", 10);
    right[n - 1] = 1;
    addStep(`Initialize right[${n - 1}] = 1.`, 11, { rightIndex: n - 1 });
    for (let i = n - 2; i >= 0; i--) {
      right[i] = right[i + 1] * nums[i + 1];
      addStep(
        `right[${i}] = right[${i + 1}] * nums[${i + 1}] = ${right[i + 1]} * ${
          nums[i + 1]
        } = ${right[i]}.`,
        13,
        { rightIndex: i }
      );
    }

    // Result pass
    addStep("Phase 3: Calculate final result.", 15);
    for (let i = 0; i < n; i++) {
      result[i] = left[i] * right[i];
      addStep(
        `result[${i}] = left[${i}] * right[${i}] = ${left[i]} * ${right[i]} = ${result[i]}.`,
        17,
        { resultIndex: i }
      );
    }

    addStep("Finished. Final result is calculated.", 18, { isFinal: true });
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(array);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
    setHighlightedLine(newSteps.length > 0 ? newSteps[0].highlightLine : -1);
  }, [array]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(steps.length > 0 ? steps[0].highlightLine : -1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setArray(testCases[index].array);
  };

  const addCustomTestCase = () => {
    try {
      const nums = JSON.parse(`[${customInput}]`);
      if (!Array.isArray(nums) || nums.some(isNaN))
        throw new Error("Input must be an array of numbers.");
      const newTestCase = { id: Date.now(), name: `Custom`, array: nums };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert(`Invalid format: ${error.message}. Use comma-separated numbers.`);
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || {};

  const renderArray = (arr, name, highlightIndex, color) => (
    <div className="mb-3">
      <h4 className="text-sm font-semibold mb-2 text-gray-700">{name}:</h4>
      <div className="flex flex-wrap gap-2">
        {array.map((_, idx) => (
          <div
            key={idx}
            className={`w-14 h-14 flex items-center justify-center border-2 rounded font-mono text-lg font-bold transition-all duration-300 ${
              highlightIndex === idx
                ? `bg-${color}-200 border-${color}-400 scale-110`
                : "bg-gray-100 border-gray-300"
            }`}
          >
            {arr[idx] !== undefined ? arr[idx] : ""}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <FileBox className="text-blue-500" /> Product of Array Except Self
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given an integer array `nums`, return an array `answer` such
                that `answer[i]` is equal to the product of all the elements of
                `nums` except `nums[i]`.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      [{tc.array.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 4 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Array: 1,2,3,4"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-4">
              {renderArray(array, "Input Array", -1, "gray")}
              {currentStepData.leftProducts &&
                renderArray(
                  currentStepData.leftProducts,
                  "Left Products",
                  currentStepData.leftIndex,
                  "blue"
                )}
              {currentStepData.rightProducts &&
                renderArray(
                  currentStepData.rightProducts,
                  "Right Products",
                  currentStepData.rightIndex,
                  "purple"
                )}
              {currentStepData.result &&
                currentStepData.result.some((v) => v !== undefined) &&
                renderArray(
                  currentStepData.result,
                  "Result",
                  currentStepData.resultIndex,
                  "green"
                )}
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The problem can be solved in linear time without using the
              division operator. The approach is to make two passes over the
              array. First, we create an array `left` where `left[i]` is the
              product of all elements to the left of `i`. Second, we create an
              array `right` where `right[i]` is the product of all elements to
              the right of `i`. The final result for index `i` is simply
              `left[i] * right[i]`.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(N)
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              (Can be optimized to O(1) space).
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The product except self is the product of all numbers to the
                  left (prefix) times the product of all numbers to the right
                  (suffix).
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Two separate passes make calculating prefix and suffix
                  products straightforward.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  This method elegantly handles zeros and negative numbers
                  without special cases (aside from multiple zeros, which isn't
                  covered by this O(1) space approach).
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductOfArrayExceptSelf;
